import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  useToast,
  Flex,
  Card,
  CardHeader,
  CardBody,
  Text,
  HStack,
  VStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Divider,
  Container,
} from "@chakra-ui/react";
import { 
  FiSearch, 
  FiHome, 
  FiCheckCircle, 
  FiXCircle, 
  FiEdit3,
  FiUsers,
  FiMapPin
} from "react-icons/fi";
import { useCognito } from "../utils/useCognito";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const FacilitiesManagementPage = () => {
  const { getIdToken } = useCognito();
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [updating, setUpdating] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [searchParams] = useSearchParams();
  const isTestMode = searchParams.get("test") === "true";

  const TEST_USER_IDS = [
    "auth0|67dca56ce48dfa7a186b2b1f",
    "auth0|67cdaa044a714c1f00addefe",
    "auth0|67ef0012b076d56b64707091",
    "auth0|67eebba8f3ef00b630525864",
    "auth0|68081737a26a556b062b0fc2",
    "auth0|67effe3acc3eee3b74316298",
    "auth0|6813d9f226ee7d3fd62139a3",
    "auth0|6838b99566a9c8dfb930f824",
  ];

  // Color mode values for consistent theming
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const searchBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const token = getIdToken();
        const res = await axios.post(
          "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData",
          { test: isTestMode },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        // Transform BAA data into facilities format
        let baaData = res.data.baaData || [];
        
        // Filter based on test mode
        if (isTestMode) {
          baaData = baaData.filter(baa => TEST_USER_IDS.includes(baa.UserId || baa.userId));
        } else {
          baaData = baaData.filter(baa => !TEST_USER_IDS.includes(baa.UserId || baa.userId));
        }

        const facilitiesData = baaData.map(baa => {
          // Parse facilityAddress if it's a stringified JSON
          let facilityAddress = baa.facilityAddress;
          if (typeof facilityAddress === "string") {
            try {
              facilityAddress = JSON.parse(facilityAddress);
            } catch {
              facilityAddress = {};
            }
          }
          
          return {
            facilityId: baa.facilityId, // Use UserId as facilityId for now
            facilityName: baa.facilityName || 'Unknown Facility',
            address: facilityAddress ? 
              `${facilityAddress.street || ''} ${facilityAddress.city || ''} ${facilityAddress.state || ''} ${facilityAddress.zip || ''}`.trim() : 
              'No address available',
            inGoodStanding: baa.inGoodStanding !== false, // Default to true if not explicitly false
            npi: facilityAddress?.npi || baa.npi || 'No NPI'
          };
        });
        
        // Sort facilities alphabetically by name
        const sortedFacilities = facilitiesData.sort((a, b) => 
          (a.facilityName || '').localeCompare(b.facilityName || '')
        );
        
        setFacilities(sortedFacilities);
        setFilteredFacilities(sortedFacilities);
      } catch (err) {
        console.error("Error fetching facilities:", err);
        toast({
          title: "Failed to load facilities",
          description: "Please check network and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchFacilities();
  }, [getIdToken, toast, isTestMode]);

  // Search functionality
  useEffect(() => {
    const filtered = facilities.filter(facility => {
      const name = (facility.facilityName || '').toLowerCase();
      const address = (facility.address || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      return name.includes(query) || address.includes(query);
    });
    setFilteredFacilities(filtered);
  }, [searchQuery, facilities]);

  const handleStatusChange = (facility) => {
    setSelectedFacility(facility);
    onOpen();
  };

  const confirmStatusChange = async () => {
    if (!selectedFacility) return;

    setUpdating(true);
    try {
      const token = getIdToken();
      const newStatus = !selectedFacility.inGoodStanding;
      
      await axios.post(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateFacilityStanding",
        {
          facilityId: selectedFacility.facilityId,
          inGoodStanding: newStatus,
          test: isTestMode
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state
      const updatedFacilities = facilities.map(facility =>
        facility.facilityId === selectedFacility.facilityId
          ? { ...facility, inGoodStanding: newStatus }
          : facility
      );
      setFacilities(updatedFacilities);

      toast({
        title: "Facility status updated",
        description: `${selectedFacility.facilityName} is now ${newStatus ? 'in good standing' : 'not in good standing'}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error updating facility status:", err);
      toast({
        title: "Failed to update facility status",
        description: "Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
      setSelectedFacility(null);
      onClose();
    }
  };

  const getStatusColor = (inGoodStanding) => {
    return inGoodStanding ? 'green' : 'red';
  };

  const getStatusIcon = (inGoodStanding) => {
    return inGoodStanding ? FiCheckCircle : FiXCircle;
  };

  const getStatusText = (inGoodStanding) => {
    return inGoodStanding ? 'Good Standing' : 'Not in Good Standing';
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" thickness="4px" />
          <Text color="gray.600">Loading facilities...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      {/* Header Section */}
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={FiHome} color="teal.500" boxSize={8} />
            <Heading size="lg" color="teal.600">
              Facilities Management {isTestMode && "(Test Mode)"}
            </Heading>
          </HStack>
          <Text color="gray.600" fontSize="md">
            Manage facility standing status and view facility information
          </Text>
        </Box>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <HStack>
                <Icon as={FiHome} color="blue.500" boxSize={6} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {facilities.length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Total Facilities</Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <HStack>
                <Icon as={FiCheckCircle} color="green.500" boxSize={6} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {facilities.filter(f => f.inGoodStanding).length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">In Good Standing</Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <HStack>
                <Icon as={FiXCircle} color="red.500" boxSize={6} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="red.600">
                    {facilities.filter(f => !f.inGoodStanding).length}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Not in Good Standing</Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Search Bar */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search facilities by name or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={searchBg}
                borderColor="gray.300"
              />
            </InputGroup>
          </CardBody>
        </Card>

        {/* Facilities Table */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader>
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="semibold">
                Facilities ({filteredFacilities.length})
              </Text>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            {filteredFacilities.length === 0 ? (
              <VStack spacing={4} py={8}>
                <Icon as={FiHome} boxSize={12} color="gray.400" />
                <Text color="gray.500" fontSize="lg">
                  {searchQuery ? 'No facilities match your search' : 'No facilities found'}
                </Text>
                {searchQuery && (
                  <Button
                    variant="ghost"
                    onClick={() => setSearchQuery('')}
                    color="teal.500"
                  >
                    Clear search
                  </Button>
                )}
              </VStack>
            ) : (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Facility Name</Th>
                    <Th>Address</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredFacilities.map((facility) => (
                    <Tr key={facility.facilityId} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <HStack>
                          <Icon as={FiHome} color="gray.500" />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold">
                              {facility.facilityName || 'Unnamed Facility'}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              ID: {facility.facilityId}
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <HStack>
                          <Icon as={FiMapPin} color="gray.400" boxSize={4} />
                          <Text fontSize="sm">
                            {facility.address || 'No address available'}
                          </Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getStatusColor(facility.inGoodStanding)}
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          <HStack spacing={1}>
                            <Icon as={getStatusIcon(facility.inGoodStanding)} boxSize={3} />
                            <Text fontSize="xs" fontWeight="medium">
                              {getStatusText(facility.inGoodStanding)}
                            </Text>
                          </HStack>
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          leftIcon={<FiEdit3 />}
                          colorScheme="teal"
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(facility)}
                        >
                          Update Status
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Update Facility Status
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack spacing={4} align="start">
                <Text>
                  Are you sure you want to change the status of{' '}
                  <Text as="span" fontWeight="bold" color="teal.600">
                    {selectedFacility?.facilityName}
                  </Text>
                  ?
                </Text>
                
                <Box p={4} bg="gray.50" borderRadius="md" w="full">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Current Status:</Text>
                    <Badge
                      colorScheme={getStatusColor(selectedFacility?.inGoodStanding)}
                      variant="subtle"
                    >
                      {getStatusText(selectedFacility?.inGoodStanding)}
                    </Badge>
                  </HStack>
                  <HStack justify="space-between" mt={2}>
                    <Text fontSize="sm" color="gray.600">New Status:</Text>
                    <Badge
                      colorScheme={getStatusColor(!selectedFacility?.inGoodStanding)}
                      variant="solid"
                    >
                      {getStatusText(!selectedFacility?.inGoodStanding)}
                    </Badge>
                  </HStack>
                </Box>

                <Text fontSize="sm" color="gray.500">
                  This action will update the facility's standing status immediately.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="teal"
                onClick={confirmStatusChange}
                ml={3}
                isLoading={updating}
                loadingText="Updating..."
              >
                Confirm Update
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default FacilitiesManagementPage;